import { useMemo, useState } from 'react';
import {
  ActionableNotification,
  Button,
  InlineLoading,
  RadioTile,
  Search,
  Tag,
  TileGroup,
} from '@carbon/react';
import { Close } from '@carbon/react/icons';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useDebouncedChange } from 'components/debounced-input/use-debounced-change';
import { Drawer } from 'components/drawer';
import type { ScenarioDomain } from 'lib/evaluation-case/scenario-domains';
import { parseMomentUtc, US_DATE_FORMAT } from 'utils/dates';
import { format0 } from 'utils/number';
import { AreaTags } from './area-tags';
import { areaTagFields } from './utils';
import { fetchPickerRows } from './api';
import {
  candidateChoice,
  pickable,
  suggestedCandidate,
} from './migration-suggestion';
import type { MigrationSuggestion, ScenarioChoice } from './types';
import styles from './case-editor.module.scss';

const NONE = 'none';

/**
 * A picker row as the tile's pick.
 *
 * @param {ScenarioChoice} row - A row of the picker's list.
 * @return {ScenarioChoice} The choice, with only the choice's own fields.
 */
const rowChoice = (row: ScenarioChoice): ScenarioChoice => ({
  id: row.id,
  name: row.name,
  ...areaTagFields(row),
});

/**
 * A failure's message when it says more than the title does.
 *
 * @param {unknown} error - The failure.
 * @param {string} title - The notification's title.
 * @return {string | undefined} The message, or undefined.
 */
function detail(error: unknown, title: string): string | undefined {
  return error instanceof Error && error.message !== title
    ? error.message
    : undefined;
}

export interface DomainPickerDrawerProps {
  domain: ScenarioDomain;
  /** The scenario the form holds for the domain, kept in the list even if archived. */
  selected: ScenarioChoice | null;
  /** Stages the choice into the form (null clears the domain). */
  onSave: (choice: ScenarioChoice | null) => void;
  onClose: () => void;
  /**
   * The migration's suggestion for the domain (11.4). With nothing picked, the drawer opens on it,
   * and lists the other candidates to pick in one click. Archived ones are shown, never offered.
   */
  suggestion?: MigrationSuggestion;
}

/**
 * Picks a domain's scenario: search, "No scenario assigned" to clear it, a tile per active
 * scenario with the areas it covers. Save stages the pick into the form; Cancel leaves it. A
 * failed load says so with Retry, rather than an empty list.
 *
 * @param {DomainPickerDrawerProps} props - The domain, current pick and handlers.
 * @return {JSX.Element} The drawer.
 */
export function DomainPickerDrawer({
  domain,
  selected,
  onSave,
  onClose,
  suggestion,
}: DomainPickerDrawerProps) {
  // The term the list is fetched for, once typing pauses; `search` is what the box shows.
  const [term, setTerm] = useState('');
  const [search, setSearch] = useDebouncedChange(term, setTerm, 300);
  const migrationChoices = suggestion
    ? [suggestedCandidate(suggestion), ...suggestion.candidates].filter(
        (candidate) => candidate.name !== null
      )
    : [];
  const preselected =
    suggestion && pickable(suggestedCandidate(suggestion))
      ? suggestion.scenarioId
      : null;
  const [pendingId, setPendingId] = useState<number | null>(
    selected?.id ?? preselected
  );
  // The row picked, kept as it was when chosen: a later search replaces the list, and the pick
  // must still save as itself.
  const [pendingRow, setPendingRow] = useState<ScenarioChoice | null>(null);
  const { data, isPending, isError, error, refetch, isPlaceholderData } =
    useQuery({
      queryKey: [
        'evaluation-case:picker',
        domain.key,
        selected?.id ?? null,
        term.trim(),
      ],
      queryFn: ({ signal }) =>
        fetchPickerRows(domain, selected?.id ?? null, term, signal),
      retry: 0,
      staleTime: 60_000,
      // A new search keeps the last list up until its own arrives.
      placeholderData: keepPreviousData,
    });
  const rows = data?.rows;
  const truncated = data ? data.totalCount > data.rows.length : false;

  // The server has answered for what the box shows: its rows are the search's, and may match on
  // more than the name, so they aren't filtered again here.
  const answered = !isPlaceholderData && term.trim() === search.trim();
  // Until then, the last list is narrowed in the browser so typing feels immediate.
  const filtered = useMemo(() => {
    const typed = search.trim().toLowerCase();
    const all = rows ?? [];
    return typed && !answered
      ? all.filter((row) => row.name.toLowerCase().includes(typed))
      : all;
  }, [rows, search, answered]);

  const pick = (id: number | null) => {
    setPendingId(id);
    const row = id === null ? undefined : rows?.find((r) => r.id === id);
    setPendingRow(row ? rowChoice(row) : null);
  };

  const handleSave = () => {
    if (pendingId === null) {
      onSave(null);
      return;
    }
    // The row as it was picked, though a later search may have left it off the list.
    const listed = rows?.find((candidate) => candidate.id === pendingId);
    const row =
      pendingRow?.id === pendingId ? pendingRow : listed && rowChoice(listed);
    // A migration choice may sit past the list's page of 500.
    const fromMigration = migrationChoices.find(
      (candidate) => candidate.id === pendingId && pickable(candidate)
    );
    onSave(
      row ? row : fromMigration ? candidateChoice(fromMigration) : selected
    );
  };

  return (
    <Drawer open direction="right" size={480} onClose={onClose} enableOverlay>
      <div className={styles.drawer}>
        <div className={styles.drawerHeader}>
          <h4 className={styles.drawerTitle}>{domain.label} scenarios</h4>
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={Close}
            iconDescription="Close"
            onClick={onClose}
          />
        </div>

        <Search
          id={`case-picker-search-${domain.key}`}
          labelText={`Search ${domain.label} scenarios`}
          placeholder="Search scenarios..."
          size="md"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />

        {/* Only for the term the box shows: while a new search loads, the count is the last one's. */}
        {data && truncated && answered && (
          <p className={styles.drawerDate} data-testid="picker-truncated">
            Showing {format0(data.rows.length)} of {format0(data.totalCount)};
            refine the search
          </p>
        )}

        {migrationChoices.length > 0 && (
          <div
            className={styles.suggestion}
            data-testid="picker-migration-suggestion"
          >
            <span className={styles.suggestionLabel}>
              Suggested from migration
            </span>
            <div className={styles.suggestionCandidates}>
              {migrationChoices.map((candidate) => {
                if (!pickable(candidate)) {
                  return (
                    <span key={candidate.id} className={styles.tileScenario}>
                      {candidate.name}
                      <Tag size="sm" type="red">
                        Archived
                      </Tag>
                    </span>
                  );
                }
                return (
                  <Button
                    key={candidate.id}
                    kind={pendingId === candidate.id ? 'primary' : 'ghost'}
                    size="sm"
                    aria-pressed={pendingId === candidate.id}
                    onClick={() => setPendingId(candidate.id)}
                  >
                    {candidate.name}
                  </Button>
                );
              })}
            </div>
          </div>
        )}

        <div className={styles.drawerList}>
          {isPending && <InlineLoading description="Loading scenarios..." />}

          {isError && (
            <ActionableNotification
              inline
              kind="error"
              hasFocus={false}
              lowContrast
              hideCloseButton
              title={`Couldn't load ${domain.label} scenarios`}
              subtitle={detail(
                error,
                `Couldn't load ${domain.label} scenarios`
              )}
              actionButtonLabel="Retry"
              onActionButtonClick={() => refetch()}
            />
          )}

          {rows && (
            <TileGroup
              name={`case-picker-${domain.key}`}
              legend={`${domain.label} scenario`}
              valueSelected={pendingId === null ? NONE : String(pendingId)}
              onChange={(value: string | number) =>
                pick(value === NONE ? null : Number(value))
              }
            >
              <RadioTile id={`case-${domain.key}-none`} value={NONE}>
                <span className={styles.tileEmpty}>No scenario assigned</span>
              </RadioTile>
              {filtered.map((row) => (
                <RadioTile
                  key={row.id}
                  id={`case-${domain.key}-${row.id}`}
                  value={String(row.id)}
                >
                  <span className={styles.tileScenario}>
                    {row.name}
                    {row.archived && (
                      <Tag size="sm" type="red">
                        Archived
                      </Tag>
                    )}
                  </span>
                  <AreaTags
                    scenario={row}
                    gatesCoverage={domain.gatesCoverage}
                  />
                  {row.updatedAt && (
                    <span className={styles.drawerDate}>
                      Updated{' '}
                      {parseMomentUtc(row.updatedAt).format(US_DATE_FORMAT)}
                    </span>
                  )}
                </RadioTile>
              ))}
            </TileGroup>
          )}

          {rows && filtered.length === 0 && (
            <p className={styles.tileEmpty}>
              {search.trim()
                ? 'No scenarios match the search.'
                : 'No scenarios yet.'}
            </p>
          )}
        </div>

        <div className={styles.drawerFooter}>
          <Button kind="secondary" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button
            kind="primary"
            size="md"
            disabled={pendingId === (selected?.id ?? null)}
            onClick={handleSave}
          >
            Save
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
